/**
 * Button — the one interactive-control recipe: `primary` / `secondary` /
 * `destructive` / `ghost` (icon button) × `sm` / `default` / `lg`.
 *
 * 2026-09 redesign: brand blue is for the ONE main action of a region; a
 * secondary is white with a hairline (Stripe). Heights are fixed (32/36/40px)
 * so a row of mixed buttons lines up, and `sm` is still ≥ 32px — row actions
 * in tables are tapped on phones through the APK.
 */
import type { ButtonHTMLAttributes } from "react";

export type ButtonVariant = "primary" | "secondary" | "destructive" | "ghost";
export type ButtonSize = "sm" | "default" | "lg";

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    "bg-brand-500 hover:bg-brand-600 active:bg-brand-700 text-white shadow-[inset_0_-1px_0_rgb(0_0_0/0.12)] disabled:opacity-50 disabled:cursor-not-allowed",
  secondary:
    "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white",
  destructive:
    "bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed",
  ghost:
    "text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent",
};

const SIZE: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-[13px]",
  default: "h-9 px-3.5 text-sm",
  lg: "h-10 px-4 text-sm",
};

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export function Button({
  variant = "primary",
  size = "default",
  className = "",
  type = "button",
  children,
  ...rest
}: Props) {
  // Ghost is always an icon button — a square that ignores `size`.
  const sizeCls = variant === "ghost" ? "w-9 h-9 p-0" : SIZE[size];
  return (
    <button
      type={type}
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg font-medium whitespace-nowrap transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 ${VARIANT[variant]} ${sizeCls} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}
